import React, { useState } from 'react';
import { Download, TrendingUp, Users, Clock, Phone, FileText, Shield, Settings, BarChart3, MessageSquare, Search, Filter, ChevronDown, Calendar, Star, Building, Eye } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import TabSystem from './TabSystem';
import { getTabsConfig } from '../config/lookerDashboards';
import FloatingParticles from './FloatingParticles';
import EnhancedCard from './EnhancedCard';
import EnhancedButton from './EnhancedButton';
import PageHeader from './ui/PageHeader';
import '../styles/neutral-theme.css';
import '../styles/reports-modern.css';

const ReportsPanel = () => {
  const { t } = useLanguage();
  const [selectedReport, setSelectedReport] = useState('campaign');
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterIndustry, setFilterIndustry] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Configure Looker report tabs using the configuration file  
  const { locale } = useLanguage();
  
  const getReportTabs = () => {
    const tabsConfig = getTabsConfig('reports', locale);
    
    // Icon mapping
    const iconMapping = {
      'FileText': FileText,
      'MessageSquare': MessageSquare,
      'Users': Users,
      'Shield': Shield,
      'BarChart3': BarChart3,
      'Settings': Settings
    };
    
    return tabsConfig.map(tab => ({
      ...tab,
      icon: iconMapping[tab.icon] || FileText
    }));
  };

  const handleReportTabChange = (tabId) => {
    // Tab changed
  };

  // Mock data for multiple campaigns
  const campaigns = [
    {
      id: 1,
      name: 'Ventas Madrid Q4 2024',
      status: 'completed',
      industry: 'retail',
      startDate: '2024-10-01',
      endDate: '2024-12-31',
      totalContacts: 5456,
      successRate: 72,
      revenue: '125,890€',
      type: 'sales',
      agent: 'VoiceBot Pro'
    },
    {
      id: 2,
      name: 'Retención Clientes Premium',
      status: 'completed',
      industry: 'banking',
      startDate: '2024-11-15',
      endDate: '2024-12-15',
      totalContacts: 2890,
      successRate: 85,
      revenue: '89,340€',
      type: 'retention',
      agent: 'ChatBot Advanced'
    },
    {
      id: 3,
      name: 'Encuesta Satisfacción',
      status: 'completed',
      industry: 'healthcare',
      startDate: '2024-12-01',
      endDate: '2024-12-20',
      totalContacts: 1250,
      successRate: 91,
      revenue: '15,680€',
      type: 'surveys',
      agent: 'Hybrid System'
    },
    {
      id: 4,
      name: 'Cobranzas Diciembre',
      status: 'active',
      industry: 'telecom',
      startDate: '2024-12-01',
      endDate: '2025-01-15',
      totalContacts: 3400,
      successRate: 67,
      revenue: '78,920€',
      type: 'collections',
      agent: 'VoiceBot Standard'
    },
    {
      id: 5,
      name: 'Soporte Técnico Q1 2025',
      status: 'scheduled',
      industry: 'automotive',
      startDate: '2025-01-01',
      endDate: '2025-03-31',
      totalContacts: 0,
      successRate: 0,
      revenue: '0€',
      type: 'support',
      agent: 'ChatBot Pro'
    }
  ];

  // Filter campaigns based on search term, status, and industry
  const filteredCampaigns = campaigns.filter(campaign => {
    const matchesSearch = campaign.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || campaign.status === filterStatus;
    const matchesIndustry = filterIndustry === 'all' || campaign.industry === filterIndustry;
    return matchesSearch && matchesStatus && matchesIndustry;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return '#22c55e';
      case 'active': return '#3b82f6';
      case 'scheduled': return '#f59e0b';
      case 'paused': return '#6b7280';
      default: return '#64748b';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'completed': return t('campaigns.completed');
      case 'active': return 'Activa';
      case 'scheduled': return 'Programada';
      case 'paused': return t('campaigns.paused');
      default: return status;
    }
  };

  const getTypeLabel = (type) => {
    return t(`campaignMgmt.types.${type}`);
  };

  const getIndustryLabel = (industry) => {
    return t(`campaignMgmt.industries.${industry}`);
  };

  // Campaign metrics for detailed report (only calculated if campaign is selected)
  const campaignMetrics = selectedCampaign ? {
    totalContacts: selectedCampaign.totalContacts,
    callAttempts: Math.round(selectedCampaign.totalContacts * 1.25),
    totalConsumption: `${Math.round(selectedCampaign.totalContacts * 1.8)} min`,
    campaignCost: `${Math.round(selectedCampaign.totalContacts * 0.28)}€`
  } : null;

  const callBreakdown = selectedCampaign ? {
    completed: { count: Math.round(selectedCampaign.totalContacts * 0.8), percentage: 80 },
    errors: { count: Math.round(selectedCampaign.totalContacts * 0.12), percentage: 12 },
    returned: { count: Math.round(selectedCampaign.totalContacts * 0.095), percentage: 9.5 },
    voicemails: { count: Math.round(selectedCampaign.totalContacts * 0.13), percentage: 13 },
    rescheduled: { count: Math.round(selectedCampaign.totalContacts * 0.088), percentage: 8.8 }
  } : null;

  const qualityMetrics = [
    { metric: t('reports.customerSatisfaction'), score: 82, rating: t('reports.veryGood') },
    { metric: t('reports.callCompliance'), score: 89, rating: t('reports.veryGood') },
    { metric: t('reports.responseComplexity'), score: 74, rating: t('reports.veryGood') },
    { metric: t('reports.clarityIndex'), score: 85, rating: t('reports.veryGood') },
    { metric: t('reports.callCompliance'), score: 96, rating: t('reports.veryGood') }
  ];

  // If no campaign is selected, show the campaign list
  if (!selectedCampaign) {
    return (
      <div className="reports-panel dashboard-container">
        <FloatingParticles count={20} />
        
        <PageHeader
          title={`${t('reports.title')} - ${t('campaigns.title')}`}
          subtitle="Selecciona una campaña para ver su informe detallado"
          icon={<FileText />}
          actions={
            <EnhancedButton variant="secondary" icon={<Download size={16} />}>
              {t('common.export')} Todo
            </EnhancedButton>
          }
        />

        {/* Filters and Search */}
        <div className="filters-section">
          <div className="search-box">
            <Search size={16} />
            <input
              type="text"
              placeholder="Buscar campañas..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          
          <div className="filter-controls">
            <select 
              value={filterStatus} 
              onChange={(e) => setFilterStatus(e.target.value)}
              className="filter-select"
            >
              <option value="all">Todos los estados</option>
              <option value="completed">Completadas</option>
              <option value="active">Activas</option>
              <option value="scheduled">Programadas</option>
              <option value="paused">Pausadas</option>
            </select>
            
            <select 
              value={filterIndustry} 
              onChange={(e) => setFilterIndustry(e.target.value)}
              className="filter-select"
            >
              <option value="all">Todas las industrias</option>
              <option value="retail">Retail</option>
              <option value="banking">Banca</option>
              <option value="healthcare">Salud</option>
              <option value="telecom">Telecomunicaciones</option>
              <option value="automotive">Automotriz</option>
            </select>
          </div>
        </div>

        {/* Campaigns Grid */}
        <div className="campaigns-grid">
          {filteredCampaigns.map(campaign => (
            <div key={campaign.id} className="campaign-card">
              <div className="campaign-header">
                <div className="campaign-info">
                  <h3 className="campaign-name">{campaign.name}</h3>
                  <div className="campaign-meta">
                    <span className="campaign-type">{getTypeLabel(campaign.type)}</span>
                    <span className="campaign-industry">{getIndustryLabel(campaign.industry)}</span>
                  </div>
                </div>
                <div 
                  className="campaign-status"
                  style={{ backgroundColor: getStatusColor(campaign.status) }}
                >
                  {getStatusLabel(campaign.status)}
                </div>
              </div>
              
              <div className="campaign-metrics">
                <div className="metric">
                  <div className="metric-label">Contactos</div>
                  <div className="metric-value">{campaign.totalContacts.toLocaleString()}</div>
                </div>
                <div className="metric">
                  <div className="metric-label">Tasa de Éxito</div>
                  <div className="metric-value">{campaign.successRate}%</div>
                </div>
                <div className="metric">
                  <div className="metric-label">Ingresos</div>
                  <div className="metric-value">{campaign.revenue}</div>
                </div>
              </div>
              
              <div className="campaign-dates">
                <div className="date-range">
                  <Calendar size={14} />
                  <span>{new Date(campaign.startDate).toLocaleDateString()} - {new Date(campaign.endDate).toLocaleDateString()}</span>
                </div>
                <div className="campaign-agent">
                  <Users size={14} />
                  <span>{campaign.agent}</span>
                </div>
              </div>
              
              <div className="campaign-actions">
                <button 
                  className="btn btn-primary"
                  onClick={() => setSelectedCampaign(campaign)}
                  disabled={campaign.status === 'scheduled'}
                >
                  <Eye size={16} />
                  {campaign.status === 'scheduled' ? 'Programada' : 'Ver Informe'}
                </button>
              </div>
            </div>
          ))}
        </div>

        {filteredCampaigns.length === 0 && (
          <div className="empty-state">
            <FileText size={48} />
            <h3>No se encontraron campañas</h3>
            <p>Intenta ajustar los filtros de búsqueda</p>
          </div>
        )}

      </div>
    );
  }

  return (
    <div className="reports-panel dashboard-container">
      <FloatingParticles count={20} />
      
      <PageHeader
        title={selectedCampaign.name}
        subtitle={`${selectedCampaign.status === 'completed' ? t('reports.finished') : 'En progreso'}: ${new Date(selectedCampaign.endDate).toLocaleDateString()}`}
        icon={<FileText />}
        actions={
          <>
            <EnhancedButton 
              size="small"
              variant="secondary"
              onClick={() => setSelectedCampaign(null)}
            >
              Volver a Lista
            </EnhancedButton>
            <EnhancedButton variant="secondary" icon={<Download size={16} />}>
              {t('reports.download')}
            </EnhancedButton>
          </>
        }
      />

      {/* Campaign Distribution */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">{t('reports.salesDistribution')}</h3>
          <p className="card-subtitle">Distribución de resultados por categoría</p>
        </div>
        <div className="card-content">
          <div className="distribution-grid">
            <div className="distribution-item scheduled">
              <div className="distribution-color"></div>
              <div className="distribution-info">
                <div className="distribution-label">{t('reports.scheduled')}</div>
                <div className="distribution-value">{Math.round(selectedCampaign.totalContacts * 0.4).toLocaleString()}</div>
              </div>
            </div>
            <div className="distribution-item interested">
              <div className="distribution-color"></div>
              <div className="distribution-info">
                <div className="distribution-label">{t('reports.interested')}</div>
                <div className="distribution-value">{Math.round(selectedCampaign.totalContacts * 0.21).toLocaleString()}</div>
              </div>
            </div>
            <div className="distribution-item pending">
              <div className="distribution-color"></div>
              <div className="distribution-info">
                <div className="distribution-label">{t('reports.pending')}</div>
                <div className="distribution-value">{Math.round(selectedCampaign.totalContacts * 0.27).toLocaleString()}</div>
              </div>
            </div>
            <div className="distribution-item rejected">
              <div className="distribution-color"></div>
              <div className="distribution-info">
                <div className="distribution-label">{t('reports.rejected')}</div>
                <div className="distribution-value">{Math.round(selectedCampaign.totalContacts * 0.12).toLocaleString()}</div>
              </div>
            </div>
          </div>
          
          <div className="distribution-bar">
            <div className="bar-segment scheduled" style={{ width: '40%' }}></div>
            <div className="bar-segment interested" style={{ width: '21%' }}></div>
            <div className="bar-segment pending" style={{ width: '27%' }}></div>
            <div className="bar-segment rejected" style={{ width: '12%' }}></div>
          </div>
        </div>
      </div>

      <div className="reports-grid">
        {/* Quality Evaluator */}
        <div className="card quality-card">
          <div className="card-header">
            <h3 className="card-title">{t('reports.evaluator')}</h3>
            <p className="card-subtitle">{t('reports.evaluatorDesc')}</p>
          </div>
          <div className="card-content">
            <div className="quality-score">
              <div className="score-circle">
                <span className="score-value">{selectedCampaign.successRate}</span>
                <span className="score-unit">pts /100</span>
              </div>
              <div className="score-label">{t('reports.globalRating')}</div>
              <div className="score-description">
                {t('reports.optimalPerformance')}
              </div>
            </div>

            <div className="quality-metrics">
              {qualityMetrics.map((metric, index) => (
                <div key={index} className="metric-row">
                  <div className="metric-info">
                    <span className="metric-name">{metric.metric}</span>
                    <span className="metric-rating">{metric.rating}</span>
                  </div>
                  <div className="metric-score">{metric.score}/100</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* General Summary */}
        <div className="card summary-card">
          <div className="card-header">
            <h3 className="card-title">{t('reports.generalSummary')}</h3>
            <p className="card-subtitle">Métricas generales de la campaña</p>
          </div>
          <div className="card-content">
            <div className="summary-metrics">
              <div className="summary-metric">
                <Users className="metric-icon" size={20} />
                <div className="metric-info">
                  <div className="metric-value">{campaignMetrics.totalContacts.toLocaleString()}</div>
                  <div className="metric-label">{t('reports.totalContacts')}</div>
                </div>
              </div>
              
              <div className="summary-metric">
                <Phone className="metric-icon" size={20} />
                <div className="metric-info">
                  <div className="metric-value">{campaignMetrics.callAttempts.toLocaleString()}</div>
                  <div className="metric-label">{t('reports.callAttempts')}</div>
                </div>
              </div>
              
              <div className="summary-metric">
                <Clock className="metric-icon" size={20} />
                <div className="metric-info">
                  <div className="metric-value">{campaignMetrics.totalConsumption}</div>
                  <div className="metric-label">{t('reports.totalConsumption')}</div>
                </div>
              </div>
              
              <div className="summary-metric">
                <TrendingUp className="metric-icon" size={20} />
                <div className="metric-info">
                  <div className="metric-value">{campaignMetrics.campaignCost}</div>
                  <div className="metric-label">{t('reports.campaignCost')}</div>
                </div>
              </div>
            </div>

            <div className="breakdown-section">
              <h4 className="breakdown-title">{t('reports.callBreakdown')}</h4>
              <div className="breakdown-grid">
                <div className="breakdown-item completed">
                  <div className="breakdown-color"></div>
                  <div className="breakdown-info">
                    <span className="breakdown-label">{t('reports.completed')}</span>
                    <span className="breakdown-value">{callBreakdown.completed.count.toLocaleString()}</span>
                    <span className="breakdown-percentage">{callBreakdown.completed.percentage}%</span>
                  </div>
                </div>

                <div className="breakdown-item errors">
                  <div className="breakdown-color"></div>
                  <div className="breakdown-info">
                    <span className="breakdown-label">{t('reports.errors')}</span>
                    <span className="breakdown-value">{callBreakdown.errors.count.toLocaleString()}</span>
                    <span className="breakdown-percentage">{callBreakdown.errors.percentage}%</span>
                  </div>
                </div>

                <div className="breakdown-item returned">
                  <div className="breakdown-color"></div>
                  <div className="breakdown-info">
                    <span className="breakdown-label">{t('reports.returned')}</span>
                    <span className="breakdown-value">{callBreakdown.returned.count.toLocaleString()}</span>
                    <span className="breakdown-percentage">{callBreakdown.returned.percentage}%</span>
                  </div>
                </div>

                <div className="breakdown-item voicemails">
                  <div className="breakdown-color"></div>
                  <div className="breakdown-info">
                    <span className="breakdown-label">{t('reports.voicemails')}</span>
                    <span className="breakdown-value">{callBreakdown.voicemails.count.toLocaleString()}</span>
                    <span className="breakdown-percentage">{callBreakdown.voicemails.percentage}%</span>
                  </div>
                </div>

                <div className="breakdown-item rescheduled">
                  <div className="breakdown-color"></div>
                  <div className="breakdown-info">
                    <span className="breakdown-label">{t('reports.rescheduled')}</span>
                    <span className="breakdown-value">{callBreakdown.rescheduled.count.toLocaleString()}</span>
                    <span className="breakdown-percentage">{callBreakdown.rescheduled.percentage}%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Looker Dashboards Section */}
      <div className="looker-section">
        <div className="section-header">
          <h2>{t('reports.detailedAnalysis')}</h2>
          <p>{t('reports.dashboardPlaceholder')}</p>
        </div>
        
        <TabSystem
          tabs={getReportTabs()}
          defaultTab={getReportTabs()[0]?.id}
          onTabChange={handleReportTabChange}
          style="modern"
        />
      </div>

    </div>
  );
};

export default ReportsPanel;